import { makeAutoObservable } from "mobx";
import { GeneratedWorkflowDto } from "../../api-client";

// ─── Types ───────────────────────────────────────────────────────────────────

export type AgentStatus = "idle" | "running" | "completed" | "error";

export type AgentName = "rescheduling" | "backplanning";

export interface AgentState {
  status: AgentStatus;
  /** Which agent is currently active. */
  agentName: AgentName | null;
  /** The workflow result returned on completion (null until completed). */
  result: unknown;
  /** Human-readable error string if status === "error". */
  errorMessage: string | null;
  /** 0–100 progress value while running. */
  progress: number;
  /** The params that were used for the last run. */
  lastParams: any;
  /** The active workflow job — stored so we can re-subscribe after remount. */
  activeWorkflow: GeneratedWorkflowDto | null;
}

const INITIAL_AGENT_STATE: AgentState = {
  status: "idle",
  agentName: null,
  result: null,
  errorMessage: null,
  progress: 0,
  lastParams: null,
  activeWorkflow: null,
};

// ─── Store ───────────────────────────────────────────────────────────────────

/**
 * Generic agent lifecycle store.
 * Tracks which agent is active, its status, progress, result, and errors.
 * Decoupled from mutation drafts — can be used by any feature that runs agents.
 */
export class AgentStore {
  state: AgentState = { ...INITIAL_AGENT_STATE };

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  // ── Mutations ──

  setRunning(agentName: AgentName, params: unknown = null) {
    this.state = {
      status: "running",
      agentName,
      progress: 0,
      result: null,
      errorMessage: null,
      lastParams: params,
      activeWorkflow: this.state.activeWorkflow,
    };
  }

  setWorkflow(workflow: GeneratedWorkflowDto) {
    this.state = { ...this.state, activeWorkflow: workflow };
  }

  setProgress(progress: number) {
    this.state = { ...this.state, progress: Math.min(100, Math.max(0, progress)) };
  }

  setCompleted(result: unknown = null) {
    this.state = {
      ...this.state,
      status: "completed",
      progress: 100,
      result,
      errorMessage: null,
      activeWorkflow: null,
    };
  }

  setError(errorMessage: string) {
    this.state = {
      ...this.state,
      status: "error",
      errorMessage,
      activeWorkflow: null,
    };
  }

  reset() {
    this.state = { ...INITIAL_AGENT_STATE };
  }

  // ── Queries ──

  get isRunning(): boolean {
    return this.state.status === "running";
  }

  get isCompleted(): boolean {
    return this.state.status === "completed";
  }

  get isError(): boolean {
    return this.state.status === "error";
  }

  get hasError(): boolean {
    return this.state.errorMessage !== null && this.state.errorMessage.length > 0;
  }

  get activeAgentName(): AgentName | null {
    return this.state.agentName;
  }

  /**
   * Returns the currently active agent if running, otherwise the provided fallback.
   */
  getActiveOrDefault(fallback: AgentName): AgentName {
    if (this.state.status === "running" && this.state.agentName) return this.state.agentName;
    return fallback;
  }
}
